// Web-side mirror of daysheets-server/lib/zoneBands.js. Keep the rule
// set + city overrides in sync — the email and the in-app timeline
// should always render bands the same way.
//
// Output shape per section:
//   { kind: "band",   zone: <iana>, label: <city>, short: <ET>, events: [...] }
//   { kind: "inline", zone: <sheet iana>,                         events: [...] }

import { ianaForTz, shortCodeForIana } from './tz';

const NEIGHBORHOOD_TO_ANCHOR = {
  // Greater Los Angeles
  'West Hollywood':   'Los Angeles',
  'Beverly Hills':    'Los Angeles',
  'Santa Monica':     'Los Angeles',
  'Pasadena':         'Los Angeles',
  'Hollywood':        'Los Angeles',
  'Burbank':          'Los Angeles',
  'Studio City':      'Los Angeles',
  'Culver City':      'Los Angeles',
  'Inglewood':        'Los Angeles',
  'Glendale':         'Los Angeles',
  'Long Beach':       'Los Angeles',
  'Malibu':           'Los Angeles',
  'Venice':           'Los Angeles',
  'Anaheim':          'Los Angeles',
  // NYC metro
  'Brooklyn':         'New York',
  'Queens':           'New York',
  'Manhattan':        'New York',
  'The Bronx':        'New York',
  'Bronx':            'New York',
  'Staten Island':    'New York',
  'Jersey City':      'New York',
  'Hoboken':          'New York',
  'Long Island City': 'New York',
  'Astoria':          'New York',
  // Bay Area
  'Berkeley':         'San Francisco',
  'Palo Alto':        'San Francisco',
  'Mountain View':    'San Francisco',
  'Sunnyvale':        'San Francisco',
  'San Mateo':        'San Francisco',
  // Boston metro
  'Cambridge':        'Boston',
  'Somerville':       'Boston',
  'Brookline':        'Boston',
  'Newton':           'Boston',
  // DC metro
  'Bethesda':         'Washington',
  'Silver Spring':    'Washington',
  'Reston':           'Washington',
  // Miami metro
  'Miami Beach':      'Miami',
  'Coral Gables':     'Miami',
  // Greater Dallas
  'Plano':            'Dallas',
  'Frisco':           'Dallas',
  'Irving':           'Dallas',
  'Fort Worth':       'Dallas',
  // Greater Chicago
  'Evanston':         'Chicago',
  'Naperville':       'Chicago',
  'Oak Park':         'Chicago',
};

export function cityFromIana(iana) {
  if (!iana) return null;
  const parts = String(iana).split('/');
  return (parts[parts.length - 1] || iana).replace(/_/g, ' ');
}

export function cityFromLocation(loc) {
  if (!loc) return null;
  const parts = String(loc).split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  const isCountry = (s) => /^(USA|U\.S\.A\.|United States|Canada|Mexico|Argentina|UK|United Kingdom|England|France|Germany|Italy|Spain|Japan|Australia|Brazil)$/i.test(s);
  const isStateOrZip = (s) => /^[A-Z]{2}(\s+\d{5}(-\d{4})?)?$/.test(s) || /^\d{5}(-\d{4})?$/.test(s);
  let trimmed = [...parts];
  while (trimmed.length > 1 && (isCountry(trimmed[trimmed.length - 1]) || isStateOrZip(trimmed[trimmed.length - 1]))) {
    trimmed.pop();
  }
  if (trimmed.length === 0) return null;
  if (parts.length === 1) return null;
  const raw = trimmed[trimmed.length - 1];
  return NEIGHBORHOOD_TO_ANCHOR[raw] || raw;
}

// Airport-code → city for band labels. cityFromIana returns the IANA's
// default reference city ("America/Chicago" → "Chicago",
// "America/Los_Angeles" → "Los Angeles") which is wrong when the band
// is actually Dallas or Las Vegas — same zone, different metro. Flight
// events typically only carry "LAS Airport" / "DFW Airport" as their
// location, so cityFromLocation returns null and we fall back to this
// map keyed off the event's IATA prefix. Keep in sync with the
// server's daysheets-server/lib/zoneBands.js map.
const IATA_TO_CITY = {
  ATL:'Atlanta', AUS:'Austin', BNA:'Nashville', BOS:'Boston', BUF:'Buffalo',
  BUR:'Burbank', BWI:'Baltimore', CLE:'Cleveland', CLT:'Charlotte', CMH:'Columbus',
  DAL:'Dallas', DCA:'Washington', DEN:'Denver', DFW:'Dallas', DTW:'Detroit',
  ELP:'El Paso', EWR:'Newark', FLL:'Fort Lauderdale', HNL:'Honolulu', HOU:'Houston',
  HPN:'White Plains', IAD:'Washington', IAH:'Houston', IND:'Indianapolis',
  JAX:'Jacksonville', JFK:'New York', LAS:'Las Vegas', LAX:'Los Angeles',
  LGA:'New York', LGB:'Long Beach', MCI:'Kansas City', MCO:'Orlando',
  MDW:'Chicago', MEM:'Memphis', MIA:'Miami', MKE:'Milwaukee', MMU:'Morristown',
  MSP:'Minneapolis', MSY:'New Orleans', OAK:'Oakland', ONT:'Ontario',
  ORD:'Chicago', PBI:'West Palm Beach', PDX:'Portland', PHL:'Philadelphia',
  PHX:'Phoenix', PIT:'Pittsburgh', PVD:'Providence', RDU:'Raleigh',
  RIC:'Richmond', RNO:'Reno', ROC:'Rochester', RSW:'Fort Myers', SAN:'San Diego',
  SAT:'San Antonio', SEA:'Seattle', SFO:'San Francisco', SJC:'San Jose',
  SLC:'Salt Lake City', SMF:'Sacramento', SNA:'Santa Ana', STL:'St. Louis',
  TEB:'Teterboro', TPA:'Tampa', TUS:'Tucson',
  YYZ:'Toronto', YVR:'Vancouver', YUL:'Montreal', YYC:'Calgary', YEG:'Edmonton', YOW:'Ottawa', YHZ:'Halifax',
  MEX:'Mexico City', CUN:'Cancún', GDL:'Guadalajara',
  EZE:'Buenos Aires', AEP:'Buenos Aires', GRU:'São Paulo', GIG:'Rio de Janeiro',
  SCL:'Santiago', LIM:'Lima', BOG:'Bogotá', UIO:'Quito', PTY:'Panama City',
  SJO:'San José', SDQ:'Santo Domingo', PUJ:'Punta Cana', NAS:'Nassau',
  LHR:'London', LGW:'London', STN:'London', LCY:'London',
  CDG:'Paris', ORY:'Paris', AMS:'Amsterdam', FRA:'Frankfurt', MUC:'Munich',
  BER:'Berlin', DUB:'Dublin', MAD:'Madrid', BCN:'Barcelona', LIS:'Lisbon',
  FCO:'Rome', MXP:'Milan', ZRH:'Zurich', GVA:'Geneva', CPH:'Copenhagen',
  ARN:'Stockholm', OSL:'Oslo', HEL:'Helsinki', VIE:'Vienna', PRG:'Prague',
  IST:'Istanbul', ATH:'Athens',
  DXB:'Dubai', AUH:'Abu Dhabi', DOH:'Doha', TLV:'Tel Aviv', CAI:'Cairo',
  JNB:'Johannesburg', CPT:'Cape Town',
  HND:'Tokyo', NRT:'Tokyo', KIX:'Osaka', ICN:'Seoul',
  PEK:'Beijing', PVG:'Shanghai', HKG:'Hong Kong', TPE:'Taipei',
  SIN:'Singapore', BKK:'Bangkok', KUL:'Kuala Lumpur',
  BOM:'Mumbai', DEL:'Delhi', SYD:'Sydney', MEL:'Melbourne', AKL:'Auckland',
};

function cityFromAirportCode(loc) {
  if (!loc) return null;
  const m = String(loc).match(/\b([A-Z]{3})\b/);
  return m ? IATA_TO_CITY[m[1]] || null : null;
}

function pickBandCity(events, iana) {
  const counts = new Map();
  for (const e of events) {
    let city = cityFromLocation(e.location);
    if (!city) city = cityFromAirportCode(e.location);
    if (!city) continue;
    counts.set(city, (counts.get(city) || 0) + 1);
  }
  if (counts.size === 0) return cityFromIana(iana);
  let best = null;
  let bestN = 0;
  for (const [city, n] of counts) {
    if (n > bestN) { best = city; bestN = n; }
  }
  return best || cityFromIana(iana);
}

function effectiveIana(event, sheetIana) {
  const evIana = ianaForTz(event?.timezone);
  return evIana || sheetIana;
}

// See server-side comment for the rule. Single-tz days render as one
// inline section (identical to before bands existed); multi-tz days
// render every run of ≥2 events as its own city/short-code band, and
// single-event outliers fall into inline with the badge.
export function groupEventsByZone(events, sheetTz) {
  const sheetIana = ianaForTz(sheetTz) || 'UTC';
  const list = events || [];
  if (!list.length) return [];

  const runs = [];
  let i = 0;
  while (i < list.length) {
    const startIana = effectiveIana(list[i], sheetIana);
    const run = [list[i]];
    i++;
    while (i < list.length && effectiveIana(list[i], sheetIana) === startIana) {
      run.push(list[i]);
      i++;
    }
    runs.push({ iana: startIana, events: run });
  }

  const bandingEngaged = runs.some(r =>
    r.iana !== sheetIana && r.events.length >= 2
  );

  const sections = [];
  for (const r of runs) {
    const isForeign = r.iana !== sheetIana;
    const shouldBand = r.events.length >= 2 && (isForeign || bandingEngaged);
    if (shouldBand) {
      sections.push({
        kind: 'band',
        zone: r.iana,
        label: pickBandCity(r.events, r.iana),
        short: shortCodeForIana(r.iana),
        events: r.events,
      });
    } else {
      sections.push({ kind: 'inline', zone: sheetIana, events: r.events });
    }
  }

  // Merge adjacent inline sections.
  const merged = [];
  for (const s of sections) {
    const prev = merged[merged.length - 1];
    if (s.kind === 'inline' && prev && prev.kind === 'inline') {
      prev.events.push(...s.events);
    } else {
      merged.push(s);
    }
  }
  return merged;
}

// Format a UTC instant in a target IANA zone as "HH:MM" (24h). Used by
// banded rows to render their time in the band's local zone instead of
// the sheet's. Keep parity with daysheets-server/lib/instant.js.
export function formatInstantInZone(instantISO, tzIana) {
  if (!instantISO) return null;
  const d = new Date(instantISO);
  if (Number.isNaN(d.getTime())) return null;
  const iana = ianaForTz(tzIana) || 'UTC';
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: iana, hour12: false, hour: '2-digit', minute: '2-digit',
    });
    const parts = fmt.formatToParts(d);
    const h = parts.find(p => p.type === 'hour').value;
    const m = parts.find(p => p.type === 'minute').value;
    return `${h}:${m}`;
  } catch {
    return null;
  }
}

// Format a UTC instant as the calendar DATE (YYYY-MM-DD) in a target
// zone. Needed when an instant straddles midnight in the target zone
// and the rendered day differs from the stored event.date — pairs with
// formatInstantInZone for full sheet-local datetime sort keys.
export function formatInstantDateInZone(instantISO, tzIana) {
  if (!instantISO) return null;
  const d = new Date(instantISO);
  if (Number.isNaN(d.getTime())) return null;
  const iana = ianaForTz(tzIana) || 'UTC';
  try {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: iana, year: 'numeric', month: '2-digit', day: '2-digit',
    });
    const parts = fmt.formatToParts(d);
    const y = parts.find(p => p.type === 'year').value;
    const mo = parts.find(p => p.type === 'month').value;
    const da = parts.find(p => p.type === 'day').value;
    return `${y}-${mo}-${da}`;
  } catch {
    return null;
  }
}
